import { FSA } from '@typings/redux';
import { limits } from 'config/pagination';
import { RSAA, RSAACreator } from 'redux-api-middleware';
import { ReportsActionType } from 'store/reports/reports.actionTypes';
import {
  SynchronizationLogFilters,
  SynchronizationLog,
  PersonalDataAccessRequestModel,
  PersonalDataAccessOtherCommuneRequestModel,
} from 'store/reports/reports.types';
import {
  UserPersonalDataChangeFiltersModel,
  DataChangeFiltersModel,
} from 'views/Panel/PersonalDataAccess/AccessList.types';

export const clearAuditLogData = (): FSA => ({
  type: ReportsActionType.CLEAR_AUDIT_LOG_DATA,
});

export const fetchAuditLog: RSAACreator<ReportsActionType> = (page: number | string) => {
  return {
    [RSAA]: {
      endpoint: 'audit-log',
      method: 'GET',
      types: [
        ReportsActionType.FETCH_AUDIT_LOG_REQUEST,
        {
          type: ReportsActionType.FETCH_AUDIT_LOG_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_AUDIT_LOG_FAILURE,
      ],
      params: {
        page,
        limit: limits.auditLog,
      },
    },
  };
};

export const fetchSchoolStat: RSAACreator<ReportsActionType> = () => {
  return {
    [RSAA]: {
      endpoint: 'stats',
      method: 'GET',
      types: [
        ReportsActionType.FETCH_SCHOOL_STAT_REQUEST,
        ReportsActionType.FETCH_SCHOOL_STAT_SUCCESS,
        ReportsActionType.FETCH_SCHOOL_STAT_FAILURE,
      ],
    },
  };
};

export const fetchPersonalDataAccessesList: RSAACreator<ReportsActionType> = (id: string, page: number | string) => {
  return {
    [RSAA]: {
      endpoint: `personal-data-accesses/${id}`,
      method: 'GET',
      types: [
        ReportsActionType.FETCH_PERSONAL_DATA_ACCESS_STUDENTS_LIST_REQUEST,
        {
          type: ReportsActionType.FETCH_PERSONAL_DATA_ACCESS_STUDENTS_LIST_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_PERSONAL_DATA_ACCESS_STUDENTS_LIST_FAILURE,
      ],
      params: {
        page,
        limit: limits.printedStudentsList,
      },
    },
  };
};

export const fetchSchoolStatDetails: RSAACreator<ReportsActionType> = (id: string) => {
  return {
    [RSAA]: {
      endpoint: `/stats/${id}`,
      method: 'GET',
      types: [
        ReportsActionType.FETCH_SCHOOL_STAT_DETAILS_REQUEST,
        ReportsActionType.FETCH_SCHOOL_STAT_DETAILS_SUCCESS,
        {
          type: ReportsActionType.FETCH_SCHOOL_STAT_DETAILS_FAILURE,
          meta: {
            ignoreNotFoundError: true,
          },
        },
      ],
    },
  };
};

export const fetchOtherCommunesStats: RSAACreator<ReportsActionType> = () => {
  return {
    [RSAA]: {
      endpoint: `reports/external-students`,
      method: 'GET',
      types: [
        ReportsActionType.FETCH_OTHER_COMMUNES_STATS_REQUEST,
        ReportsActionType.FETCH_OTHER_COMMUNES_STATS_SUCCESS,
        ReportsActionType.FETCH_OTHER_COMMUNES_STATS_FAILURE,
      ],
    },
  };
};

export const fetchOtherCommunesStudents: RSAACreator<ReportsActionType> = (unitId: string, communeId: string) => {
  return {
    [RSAA]: {
      endpoint: `reports/external-students/${unitId}/${communeId}`,
      method: 'GET',
      types: [
        ReportsActionType.FETCH_OTHER_COMMUNES_STUDENTS_REQUEST,
        ReportsActionType.FETCH_OTHER_COMMUNES_STUDENTS_SUCCESS,
        ReportsActionType.FETCH_OTHER_COMMUNES_STUDENTS_FAILURE,
      ],
    },
  };
};

export const clearOtherCommunesStudentsList = () => {
  return {
    type: ReportsActionType.CLEAR_OTHER_COMMUNES_STUDENTS,
  };
};

export const fetchSubventionFactors: RSAACreator<ReportsActionType> = () => {
  return {
    [RSAA]: {
      endpoint: `subvention-factors`,
      method: 'GET',
      types: [
        ReportsActionType.FETCH_SUBVENTION_FACTORS_REQUEST,
        ReportsActionType.FETCH_SUBVENTION_FACTORS_SUCCESS,
        {
          type: ReportsActionType.FETCH_SUBVENTION_FACTORS_FAILURE,
          meta: { ignoreNotFoundError: true },
        },
      ],
    },
  };
};

export const fetchSynchronizationLog: RSAACreator<ReportsActionType> = (
  page: number,
  filters: SynchronizationLogFilters,
) => {
  return {
    [RSAA]: {
      endpoint: 'sync-log',
      method: 'GET',
      types: [
        {
          type: ReportsActionType.FETCH_SYNCHRONIZATION_LOG_REQUEST,
          meta: { page, takeLatest: true },
        },
        {
          type: ReportsActionType.FETCH_SYNCHRONIZATION_LOG_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_SYNCHRONIZATION_LOG_FAILURE,
      ],
      params: {
        limit: limits.synchronizationsLog,
        ...filters,
      },
    },
  };
};

export const setSynchronizationLogFilters = (filters: SynchronizationLogFilters): FSA<ReportsActionType> => {
  return {
    type: ReportsActionType.SET_SYNCHRONIZATION_LOG_FILTERS,
    payload: filters,
  };
};

export const getSynchronizationLog = (page: number, filters: SynchronizationLogFilters): FSA<ReportsActionType> => {
  return {
    type: ReportsActionType.GET_SYNCHRONIZATION_LOG,
    payload: {
      page,
      filters,
    },
  };
};

export const setSynchronizationLogLastItem = (item: SynchronizationLog): FSA<ReportsActionType> => {
  return {
    type: ReportsActionType.SET_SYNCHRONIZATION_LOG_LAST_ITEM,
    payload: item,
  };
};

export const clearSynchronizationLogData = (): FSA => ({
  type: ReportsActionType.CLEAR_SYNCHRONIZATION_LOG_DATA,
});

export const clearPersonalDataAccess = (): FSA => ({
  type: ReportsActionType.CLEAR_PERSONAL_DATA_ACCESS_CONTAINER,
});

export const clearUserPersonalDataChangeRegister = (): FSA => ({
  type: ReportsActionType.CLEAR_USERS_PERSONAL_DATA_CHANGE_REGISTER_CONTAINER,
});

export const clearStudentChildDataChangeRegister = (): FSA => ({
  type: ReportsActionType.CLEAR_DATA_CHANGE_REGISTER,
});

export const fetchPersonalDataAccessList: RSAACreator<ReportsActionType> = (page: number | string) => {
  return {
    [RSAA]: {
      endpoint: 'personal-data-accesses',
      method: 'GET',
      types: [
        ReportsActionType.FETCH_PERSONAL_DATA_REQUEST,
        {
          type: ReportsActionType.FETCH_PERSONAL_DATA_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_PERSONAL_DATA_FAILURE,
      ],
      params: {
        page,
        limit: limits.personalDataAccessList,
      },
    },
  };
};

export const fetchDataChangeRegisterList: RSAACreator<ReportsActionType> = (
  page: number | string,
  filters: DataChangeFiltersModel,
) => {
  return {
    [RSAA]: {
      endpoint: 'personal-data-changes',
      method: 'GET',
      types: [
        ReportsActionType.FETCH_DATA_CHANGE_REGISTER_REQUEST,
        {
          type: ReportsActionType.FETCH_DATA_CHANGE_REGISTER_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_DATA_CHANGE_REGISTER_FAILURE,
      ],
      params: {
        page,
        limit: limits.personalDataAccessList,
        ...filters,
      },
    },
  };
};

export const fetchUserPersonalDataChangeRegisterList: RSAACreator<ReportsActionType> = (
  page: number,
  filters: UserPersonalDataChangeFiltersModel,
) => {
  return {
    [RSAA]: {
      endpoint: 'users-personal-data-changes',
      method: 'GET',
      types: [
        {
          type: ReportsActionType.FETCH_USERS_PERSONAL_DATA_CHANGE_REGISTER_REQUEST,
          meta: {
            takeLatest: true,
          },
        },
        {
          type: ReportsActionType.FETCH_USERS_PERSONAL_DATA_CHANGE_REGISTER_SUCCESS,
          meta: { page },
        },
        ReportsActionType.FETCH_USERS_PERSONAL_DATA_CHANGE_REGISTER_FAILURE,
      ],
      params: {
        page,
        limit: limits.usersPersonalDataAccessList,
        ...filters,
      },
    },
  };
};

export const setUserPersonalDataChangeRegisterListFilters = (
  filters: UserPersonalDataChangeFiltersModel,
): FSA<ReportsActionType> => {
  return {
    type: ReportsActionType.SET_USERS_PERSONAL_DATA_CHANGE_REGISTER_FILTERS,
    payload: filters,
  };
};

export const postPersonalDataAccessOtherCommune = (body: PersonalDataAccessOtherCommuneRequestModel) => {
  return {
    [RSAA]: {
      endpoint: `/personal-data-accesses/other-commune`,
      method: 'POST',
      types: [
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_OTHER_COMMUNE_REQUEST,
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_OTHER_COMMUNE_SUCCESS,
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_OTHER_COMMUNE_FAILURE,
      ],
      body,
      params: {},
    },
  };
};

export const postPersonalDataAccessPrintout = (body: PersonalDataAccessRequestModel) => {
  return {
    [RSAA]: {
      endpoint: `/personal-data-accesses/printouts`,
      method: 'POST',
      types: [
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_PRINTOUT_REQUEST,
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_PRINTOUT_SUCCESS,
        ReportsActionType.POST_PERSONAL_DATA_ACCESS_PRINTOUT_FAILURE,
      ],
      body,
      params: {},
    },
  };
};

export const setDataChangeRegisterListFilters = (filters: DataChangeFiltersModel): FSA<ReportsActionType> => {
  return {
    type: ReportsActionType.SET_DATA_CHANGE_REGISTER_FILTERS,
    payload: filters,
  };
};
